import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowUpDown, Download, ListChecks, Loader2, Pencil, RefreshCw, RotateCcw, Sparkles } from "lucide-react";

import BoardPackArranger from "./BoardPackArranger";
import BuilderReportCard from "./charts/BuilderReportCard";
import ReportDefinitionEditor from "./ReportDefinitionEditor";
import ReportDrilldownSheet from "./ReportDrilldownSheet";
import RevenueReportCard from "./charts/RevenueReportCard";
import { CAPTURE_WIDTH_PX, downloadBoardPackPdf } from "../lib/boardPackPdf";
import {
  fetchBoardPack,
  fetchReportDefinitions,
  reorderReportDefinitions,
  resetReportDefinitions,
  runReportDefinition,
  seedReportDefinitions,
  updateReportDisplay,
} from "../lib/api";

/**
 * The board pack: every report flagged for it, rendered in display order and
 * printable as one document.
 *
 * Single tenant, so this is one template with no theming layer. "Download"
 * builds a PDF from the pack as it is on screen (see lib/boardPackPdf).
 */
const EXCLUDED_KEY = (portalId, team) => `boardPack:excluded:${portalId}:${team || "all"}`;

export function loadExcluded(portalId, team) {
  try {
    const raw = window.localStorage.getItem(EXCLUDED_KEY(portalId, team));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveExcluded(portalId, team, keys) {
  try {
    window.localStorage.setItem(EXCLUDED_KEY(portalId, team), JSON.stringify(keys));
  } catch {
    /* a browser with storage turned off still downloads the pack */
  }
}

export default function BoardPackPanel({
  portalId,
  portalName,
  fyStartMonth = 7,
  // A dashboard for one business reads every report as that team, and the
  // per-report team control is hidden: the tab has already said which
  // business this is, and a report left on the other one would be wrong
  // without looking wrong.
  team = null,
  teamLabel = null,
  packName = "Strategic Development Dashboard",
}) {
  // The dashboard reads the year we are in. It used to be a dropdown, but
  // most of these reports are a picture of today — a RAG status or a renewal
  // forecast has no 2024/25 version — so only the revenue report can honestly
  // answer for another year, and it now carries its own FY comparison.
  const fiscalYear = null;
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Definitions are loaded alongside the run results, because editing in place
  // needs the definition and the pack only carries what each report produced.
  const [definitions, setDefinitions] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [arranging, setArranging] = useState(false);
  // The PDF is built from the pack as it is on screen, laid out at a fixed
  // width while it is captured.
  const packRef = useRef(null);
  const [exporting, setExporting] = useState(null);
  // Reports left out of the download. Kept per portal in the browser, so a
  // pack prepared for a meeting survives a refresh, and shown on screen as
  // "left out" rather than hidden, so nothing disappears without saying so.
  const [excluded, setExcluded] = useState(() => loadExcluded(portalId, team));
  const [choosing, setChoosing] = useState(false);
  const toggleExcluded = useCallback((key) => {
    setExcluded((current) => {
      const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      saveExcluded(portalId, team, next);
      return next;
    });
  }, [portalId, team]);

  const load = useCallback(
    async (force = false) => {
      if (!portalId) return;
      setLoading(true);
      setError(null);
      try {
        const [packData, definitionData] = await Promise.all([
          fetchBoardPack(portalId, { force, fiscalYear, team }),
          fetchReportDefinitions(portalId),
        ]);
        setPack(packData);
        setDefinitions(
          Object.fromEntries((definitionData.definitions || []).map((d) => [d.key, d])),
        );
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not build the board pack.");
      } finally {
        setLoading(false);
      }
    },
    [portalId, fiscalYear, team],
  );

  // The thirteen board reports ship as definitions, but they still have to be
  // installed into this portal. An empty pack is almost always that, not a
  // portal with nothing in it, so offer the install rather than instructions.
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (!portalId) return;
    setResetting(true);
    setError(null);
    try {
      await resetReportDefinitions(portalId);
      await load(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset the reports.");
    } finally {
      setResetting(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setError(null);
    try {
      setSeedResult(await seedReportDefinitions(portalId));
      await load(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not install the board reports.");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Install the board reports automatically the first time a portal shows an
  // empty pack. Seeding never overwrites an existing definition, so this is
  // safe to run on its own; the attempt is tracked so a portal deliberately
  // emptied does not get them reinstalled on every visit.
  useEffect(() => {
    if (!portalId || loading || seeding) return;
    if (!pack || pack.reports.length || seedResult) return;
    handleSeed();
    // handleSeed is stable for a given portal; re-running on its identity
    // would loop when the seed legitimately installs nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId, pack, loading, seeding, seedResult]);

  // Saving a target re-runs the pack so the reference line and the bars are
  // read from the same run, rather than patching the number in place and
  // leaving the chart showing a figure the server has not agreed to.
  const handleTargetChange = useCallback(
    async (key, target) => {
      await updateReportDisplay(portalId, key, { target });
      await load();
    },
    [portalId, load],
  );

  const handleDownload = async () => {
    if (!pack || !packRef.current) return;
    // Out of edit and arrange modes, so the PDF holds reports, not forms.
    setEditingKey(null);
    setArranging(false);
    setError(null);
    setExporting({ done: 0, total: pack.reports.filter((r) => !excluded.includes(r.key)).length });
    const now = new Date();
    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      await downloadBoardPackPdf(
        packRef.current,
        {
          portalName: pack.portal_name || portalName,
          packName,
          team: teamLabel,
          fiscalYear: pack.fiscal_year,
          // The month, not the minute it was pulled: a pack is discussed as
          // "September", and an exact date invites questions about the hour.
          dateLabel: now.toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
          fileDate: now.toISOString().slice(0, 7),
        },
        (done, total) => setExporting({ done, total }),
      );
    } catch (err) {
      setError("Could not build the PDF. Try again, or refresh the pack first.");
      // eslint-disable-next-line no-console
      console.error("board pack PDF", err);
    } finally {
      setExporting(null);
    }
  };

  // Team and Group products live on each report. The choice is saved on it
  // and only that report re-runs, so the rest of the pack does not flicker.
  const [busyKey, setBusyKey] = useState(null);
  const handleControlChange = useCallback(
    async (key, patch) => {
      setBusyKey(key);
      setError(null);
      try {
        await updateReportDisplay(portalId, key, patch);
        const current = (pack?.reports || []).find((r) => r.key === key);
        if (current?.deferred_to === "revenue") {
          await load(false);
        } else {
          const fresh = await runReportDefinition(
            portalId, key, false, fiscalYear ?? pack?.fiscal_year_start, team,
          );
          setPack((previous) => previous && {
            ...previous,
            reports: previous.reports.map((r) => (r.key === key ? { ...fresh, section: r.section } : r)),
          });
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not update that report.");
      } finally {
        setBusyKey(null);
      }
    },
    [portalId, pack, fiscalYear, load, team],
  );

  // A table's sort is saved as it is clicked, without re-running the pack:
  // the table has already re-sorted itself, and this keeps it for next time
  // and for the PDF.
  const handleDisplayChange = useCallback(
    (key, patch) => {
      updateReportDisplay(portalId, key, patch).catch(() => {});
    },
    [portalId],
  );

  if (!portalId) {
    return <p className="text-sm text-[#4a5b68]">Connect a HubSpot portal to build the board pack.</p>;
  }

  const generated = pack?.generated_at ? new Date(pack.generated_at) : null;

  return (
    <div className="space-y-5" data-testid="board-pack-panel">
      <style>{`
        @media print {
          /* Hide the app chrome so the pack prints as its own document. */
          .print\\:hidden, [data-testid="app-sidebar"], nav { display: none !important; }
          .board-pack-report { break-inside: avoid; page-break-inside: avoid; }
          .board-pack-page-break { break-before: page; page-break-before: always; }
          body { background: #ffffff !important; }
        }
        @page { size: A4 portrait; margin: 14mm; }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-[#4a5b68]">
            {pack?.fiscal_year ? `Fiscal year ${pack.fiscal_year}` : ""}
          </p>
          <p className="text-sm text-[#4a5b68]">
            {pack ? `${pack.reports.length} reports` : "Building…"}
            {generated ? ` — generated ${generated.toLocaleString("en-GB")}` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-sm font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996] disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh from HubSpot
          </button>
          <button
            type="button"
            onClick={() => setArranging((value) => !value)}
            disabled={loading || !pack?.reports?.length}
            className={`inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold disabled:opacity-50 ${
              arranging
                ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                : "border-[#cccccc] bg-white text-[#4a5b68] hover:border-[#57c1a9] hover:text-[#119996]"
            }`}
            data-testid="arrange-board-pack"
          >
            <ArrowUpDown className="h-4 w-4" /> Arrange
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={resetting || loading}
            title="Put every report back to its current definition. A target you have typed survives."
            className="inline-flex items-center gap-2 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-sm font-semibold text-[#4a5b68] hover:border-[#57c1a9] hover:text-[#119996] disabled:opacity-50"
            data-testid="reset-board-reports"
          >
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            Reset reports
          </button>
          <button
            type="button"
            onClick={() => setChoosing((value) => !value)}
            disabled={loading || !pack?.reports?.length}
            className={`inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold disabled:opacity-50 ${
              choosing
                ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                : "border-[#cccccc] bg-white text-[#4a5b68] hover:border-[#57c1a9] hover:text-[#119996]"
            }`}
            data-testid="choose-board-pack-reports"
          >
            <ListChecks className="h-4 w-4" />
            {excluded.length
              ? `Choose reports (${(pack?.reports?.length || 0) - excluded.length} of ${pack?.reports?.length || 0})`
              : "Choose reports"}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={loading || !pack || Boolean(exporting)}
            className="inline-flex items-center gap-2 rounded-full bg-[#50e3c2] px-3 py-1.5 text-sm font-semibold text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50 uppercase tracking-[0.12em]"
            data-testid="download-board-pack"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting
              ? `Building PDF… ${exporting.done} of ${exporting.total}`
              : "Download board pack"}
          </button>
        </div>
      </div>

      {error && (
        <p className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </p>
      )}

      {/* Which reports go in the download. Untick one and it stays on screen,
          marked, rather than vanishing. Nothing is saved to the report itself,
          so this is a decision about one pack, not an edit to the pack. */}
      {choosing && pack && (
        <div className="rounded-md border border-[#cccccc] bg-white p-4" data-testid="report-chooser">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-[#323c44]">Reports to include in the download</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setExcluded([]); saveExcluded(portalId, team, []); }}
                className="rounded-full border border-[#cccccc] px-3 py-1 text-xs font-semibold text-[#4a5b68] hover:border-[#57c1a9] hover:text-[#119996]"
              >
                Include all
              </button>
              <button
                type="button"
                onClick={() => setChoosing(false)}
                className="rounded-full border border-[#cccccc] px-3 py-1 text-xs font-semibold text-[#4a5b68] hover:border-[#57c1a9] hover:text-[#119996]"
              >
                Done
              </button>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(pack.sections || []).map((section) => (
              <div key={section}>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7d8b96]">
                  {section}
                </p>
                {(pack.reports || [])
                  .filter((report) => (report.section || "Reports") === section)
                  .map((report) => (
                    <label key={report.key} className="flex items-start gap-2 py-1 text-sm text-[#323c44]">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!excluded.includes(report.key)}
                        onChange={() => toggleExcluded(report.key)}
                        data-testid={`include-report-${report.key}`}
                      />
                      <span>{report.title}</span>
                    </label>
                  ))}
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-[#7d8b96]">
            This only changes the download. Every report stays on screen, and nothing is changed in HubSpot.
          </p>
        </div>
      )}

      {/* ── Cover ── */}
      <header className="rounded-md border border-[#cccccc] bg-white p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#119996]">
          {packName}
        </p>
        <h1 className="mt-1 font-sans text-2xl font-bold text-[#323c44]">
          {pack?.portal_name || portalName || "Portal"}
        </h1>
        <p className="mt-1 text-sm text-[#4a5b68]">
          {pack?.fiscal_year ? `Fiscal year ${pack.fiscal_year}` : ""}
          {pack?.fiscal_year && generated ? " — " : ""}
          {generated
            ? generated.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
            : ""}
        </p>
      </header>

      {arranging && pack && (
        <BoardPackArranger
          sections={pack.sections || []}
          reports={pack.reports || []}
          onCancel={() => setArranging(false)}
          onSave={async (keys, sections) => {
            await reorderReportDefinitions(portalId, keys, sections);
            setArranging(false);
            await load(false);
          }}
        />
      )}

      {loading && !pack && (
        <p className="flex items-center justify-center gap-2 py-16 text-sm text-[#4a5b68]">
          <Loader2 className="h-4 w-4 animate-spin" /> Running reports…
        </p>
      )}

      {/* Grouped into Raph's sections, in the order their first report
          appears — so reordering reports reorders sections too, with no second
          list to keep in step. */}
      <div
        ref={packRef}
        className="space-y-5"
        style={exporting ? { width: CAPTURE_WIDTH_PX, maxWidth: "none" } : undefined}
      >
      {(pack?.sections || []).map((section, sectionIndex) => (
        <section
          key={section}
          data-pack-section={section}
          className={sectionIndex > 0 ? "board-pack-page-break" : undefined}
        >
          <h2 className="mb-3 border-b border-[#cccccc] pb-2 font-sans text-lg font-bold text-[#323c44]">
            {section}
          </h2>
          <div className="space-y-5">
            {(pack?.reports || [])
              .filter((report) => (report.section || "Reports") === section)
              .map((report) => (
                <div
                  key={report.key}
                  className={`board-pack-report ${
                    excluded.includes(report.key) ? "opacity-60" : ""
                  }`}
                  // No attribute means the PDF walk does not find it, so an
                  // excluded report is on screen but out of the download.
                  data-pack-report={excluded.includes(report.key) ? undefined : report.title}
                >
                  {excluded.includes(report.key) && (
                    <p className="mb-2 flex items-center justify-between gap-3 rounded border border-[#cfa77b] bg-[#fbf6f0] px-3 py-1.5 text-xs text-[#4a5b68] print:hidden">
                      <span>Left out of the download.</span>
                      <button
                        type="button"
                        onClick={() => toggleExcluded(report.key)}
                        className="font-semibold text-[#119996] hover:underline"
                      >
                        Put it back
                      </button>
                    </p>
                  )}
                  {editingKey === report.key && definitions[report.key] ? (
                    // Edited in place, against the same stored definition the
                    // builder edits, so a tweak here shows up everywhere.
                    <ReportDefinitionEditor
                      portalId={portalId}
                      definition={definitions[report.key]}
                      fyStartYear={fiscalYear ?? pack?.fiscal_year_start}
                      fyStartMonth={pack?.fy_start_month || fyStartMonth}
                      teamPipelines={report.team_pipelines}
                      onSaved={async () => {
                        await load(false);
                      }}
                      onCancel={() => setEditingKey(null)}
                    />
                  ) : report.deferred_to === "revenue" ? (
                    // Revenue is not recomputed here: this card calls the
                    // existing MRR endpoint with the report's own filters, so
                    // month attribution and live RPI stay put.
                    <RevenueReportCard
                      portalId={portalId}
                      report={report}
                      fyStartYear={fiscalYear ?? pack?.fiscal_year_start}
                      fyStartMonth={pack?.fy_start_month || fyStartMonth}
                      teams={pack?.teams || []}
                      showTeam={!team}
                      onControlChange={handleControlChange}
                      controlsBusy={busyKey === report.key}
                    >
                      {definitions[report.key] && (
                        <button
                          type="button"
                          onClick={() => setEditingKey(report.key)}
                          className="rounded p-1.5 text-[#a9b3bb] hover:bg-[#f5f5f5] hover:text-[#119996] print:hidden"
                          title={`Edit ${report.title}`}
                          data-testid={`edit-report-${report.key}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </RevenueReportCard>
                  ) : (
                    <BuilderReportCard
                      result={report}
                      onTargetChange={handleTargetChange}
                      onDisplayChange={handleDisplayChange}
                      onControlChange={handleControlChange}
                      teams={pack?.teams || []}
                      showTeam={!team}
                      controlsBusy={busyKey === report.key}
                      onGroupClick={(group) =>
                        setDrilldown({ key: report.key, title: report.title, group })
                      }
                    >
                      {definitions[report.key] && (
                        <button
                          type="button"
                          onClick={() => setEditingKey(report.key)}
                          className="rounded p-1.5 text-[#a9b3bb] hover:bg-[#f5f5f5] hover:text-[#119996] print:hidden"
                          title={`Edit ${report.title}`}
                          data-testid={`edit-report-${report.key}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </BuilderReportCard>
                  )}
                </div>
              ))}
          </div>
        </section>
      ))}
      </div>

      <ReportDrilldownSheet
        portalId={portalId}
        reportKey={drilldown?.key}
        title={drilldown?.title}
        group={drilldown?.group}
        open={Boolean(drilldown)}
        onClose={() => setDrilldown(null)}
        fiscalYear={fiscalYear ?? pack?.fiscal_year_start}
      />

      {pack && !pack.reports.length && (
        <div className="rounded-md border border-dashed border-[#cccccc] bg-white px-4 py-12 text-center">
          <p className="text-sm text-[#4a5b68]">
            The thirteen board reports are ready to install into this portal.
          </p>
          <p className="mx-auto mt-1 max-w-lg text-xs text-[#7d8b96]">
            They install as editable definitions — filters, grouping and charts can all be changed in the
            report builder afterwards. Running this again later never reverts an edit.
          </p>
          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#50e3c2] px-4 py-2 text-sm font-semibold text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50 uppercase tracking-[0.12em]"
            data-testid="install-board-reports"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Install the board reports
          </button>
          {seedResult && (
            <p className="mt-3 text-xs text-[#4a5b68]">
              Installed {seedResult.installed.length}
              {seedResult.skipped.length ? `, kept ${seedResult.skipped.length} existing` : ""}
              {seedResult.failed.length ? `, ${seedResult.failed.length} failed` : ""}.
            </p>
          )}
          {seedResult?.failed?.length > 0 && (
            <ul className="mx-auto mt-2 max-w-lg space-y-1 text-left text-xs text-red-600">
              {seedResult.failed.map((failure) => (
                <li key={failure.key}>
                  {failure.key}: {failure.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
